import { zodResolver } from "@hookform/resolvers/zod";
import { orderBy, where } from "firebase/firestore";
import React from "react";
import { Alert, Button, Card, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaSave } from "react-icons/fa";
import { useNavigate } from "react-router";
import { z } from "zod";

import * as api from "../../api/api";
import { useOrganizationCollection } from "../../api/firestoreWrappers.generated.jsx";
import CancelButton from "../../components/CancelButton";
import BooleanFormGroup from "../../components/FormGroups/BooleanFormGroup.jsx";
import SelectFormGroup from "../../components/FormGroups/SelectFormGroup.jsx";
import TextFormGroup from "../../components/FormGroups/TextFormGroup.jsx";
import Layout, { Title } from "../../components/Layout/Layout";
import UserMakeAdminModal from "../../components/UserMakeAdminModal/index.jsx";
import { useAuth } from "../../contexts/auth_provider";
import urls from "../../urls.jsx";

export default function UserCreatePage() {
    const navigate = useNavigate();
    const [organizations] = useOrganizationCollection([
        where("deleted", "==", false),
        orderBy("name", "asc"),
    ]);
    const { resetPasswordLink } = useAuth();
    const [autoSendEmail, setAutoSendEmail] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [showUserMakeAdminModal, setShowUserMakeAdminModal] =
        React.useState(false);
    const [pendingUserCreate, setPendingUserCreate] = React.useState(null);

    const organizationOptions = organizations.map((organization) => ({
        id: organization?.id,
        name: organization?.name,
    }));

    const userSettingsConfigSchema = z.object({
        email: z.string().email(),
        organizationId: z
            .string()
            .min(1, { message: "Organization is required" })
            .refine((val) => val !== "none", {
                message: "Organization is required",
            }),
        isAdmin: z.boolean(),
    });

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(userSettingsConfigSchema),
        defaultValues: {
            email: "",
            organizationId: "none",
            isAdmin: false,
        },
    });

    const organizationId = watch("organizationId");

    const onSubmit = async (settings) => {
        if (
            settings.isAdmin &&
            settings.organizationId === api.CORVUS_ORGANIZATION_ID
        ) {
            setPendingUserCreate(settings);
            setShowUserMakeAdminModal(true);
            return;
        }

        await submitUserCreate(settings);
    };

    const submitUserCreate = async (settings) => {
        const payload = { ...settings };

        if (payload.organizationId !== api.CORVUS_ORGANIZATION_ID) {
            payload.isAdmin = false;
        }

        try {
            await api.createUser(payload);

            if (autoSendEmail) {
                await resetPasswordLink(payload.email);
            }

            navigate(`${urls.users()}?order=asc&sortCriteria=created`);
        } catch (err) {
            setError(err.message ?? String(error));
        }
    };

    return (
        <Layout>
            <title>{`Create User | MyCorvus`}</title>
            <UserMakeAdminModal
                userEmail={pendingUserCreate?.email}
                show={showUserMakeAdminModal}
                onCancel={() => {
                    setPendingUserCreate(null);
                    setShowUserMakeAdminModal(false);
                }}
                onProceed={async () => {
                    await submitUserCreate(pendingUserCreate);
                    setPendingUserCreate(null);
                    setShowUserMakeAdminModal(false);
                }}
            />
            <Card>
                <Title title={"Create user"} backButtonUrl={urls.users()} />
                <Card.Body className="p-4 pt-0">
                    {error && (
                        <Alert
                            variant="danger"
                            onClose={() => setError("")}
                            dismissible
                        >
                            {error}
                        </Alert>
                    )}
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <TextFormGroup
                            label="Email address"
                            name="email"
                            control={control}
                            errors={errors}
                            setValue={setValue}
                            placeholder="Enter email address"
                        />
                        <SelectFormGroup
                            label="Organization"
                            name="organizationId"
                            control={control}
                            errors={errors}
                            setValue={setValue}
                            disabledOption={{
                                id: "none",
                                name: "Choose an organization",
                            }}
                            options={organizationOptions}
                        />
                        {organizationId === api.CORVUS_ORGANIZATION_ID && (
                            <BooleanFormGroup
                                label="User is admin"
                                name="isAdmin"
                                control={control}
                                errors={errors}
                                setValue={setValue}
                            />
                        )}
                        <Form.Check
                            type="checkbox"
                            className="mb-3"
                            label="Automatically send password reset email after creation"
                            onChange={(e) => setAutoSendEmail(e.target.checked)}
                            checked={autoSendEmail}
                        />
                        <Card.Body className="p-0 pt-2">
                            <Button
                                variant="success"
                                disabled={isSubmitting || !isDirty}
                                type="submit"
                                className="me-2"
                            >
                                <FaSave className="mb-1 me-1" />
                                {isSubmitting ? "Creating..." : "Create"}
                            </Button>
                            <CancelButton to={urls.users()} />
                        </Card.Body>
                    </Form>
                </Card.Body>
            </Card>
        </Layout>
    );
}
